


import axios from "axios";
import { api } from "../config";
import { fetchAuthSession } from "aws-amplify/auth";
// import { Auth } from "aws-amplify";

/* =====================================================
   BASE CONFIG
===================================================== */

const token = localStorage.getItem("token");

axios.defaults.baseURL = api.API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";

if (token) {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
}

/* =====================================================
   REFRESH TOKEN FUNCTION (SHARED)
===================================================== */

const refreshToken = async () => {
  try {
    const session = await fetchAuthSession();
    const newToken = session?.tokens?.idToken || "";

    if (!newToken) throw new Error("Token refresh failed");

    localStorage.setItem("token", newToken);
    setAuthorization(newToken);
    return newToken;
  } catch (error) {
    await Auth.signOut();
    window.location.href = "/logout";
    throw error;
  }
};

/* =====================================================
   DEFAULT AXIOS INTERCEPTOR
===================================================== */

axios.interceptors.response.use(
  (response) => response.data ?? response,
  async (error) => {
    const errorData = error.response?.data;

    if (
      error.response?.status === 401 &&
      (
        errorData?.message === "The incoming token has expired" ||
        errorData?.message === "Unauthorized"
      )
    ) {
      try {
        const newToken = await refreshToken();
        error.config.headers.Authorization = "Bearer " + newToken;
        return axios.request(error.config);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject({
      message: errorData?.message || error.message,
      errorData,
    });
  }
);

/* =====================================================
   SET AUTH HEADER
===================================================== */

const setAuthorization = (token) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  ChatbotClient.defaults.headers.common["Authorization"] = "Bearer " + token;
  ElectricalChatbotClient.defaults.headers.common["Authorization"] = "Bearer " + token;
};

/* =====================================================
   CHATBOT AXIOS CLIENT
===================================================== */

const ChatbotClient = axios.create({
  baseURL:
    process.env.REACT_APP_CHATBOT_API_URL ||
    api.CHATBOT_API_URL ||
    api.API_URL,
  headers: {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  },
});

/* =====================================================
   🔥 CHATBOT INTERCEPTOR (QUICK FIX)
===================================================== */

ChatbotClient.interceptors.response.use(
  (response) => response.data ?? response,
  async (error) => {
    const errorData = error.response?.data;

    if (
      error.response?.status === 401 &&
      (
        errorData?.message === "The incoming token has expired" ||
        errorData?.message === "Unauthorized"
      )
    ) {
      try {
        const newToken = await refreshToken();
        error.config.headers.Authorization = "Bearer " + newToken;
        return ChatbotClient.request(error.config);
      } catch {
        window.location.href = "/logout";
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

/* =====================================================
   S3 CLIENT (NO INTERCEPTOR)
===================================================== */

const S3Client = axios.create({});

/* =====================================================
   ELECTRICAL CHATBOT AXIOS CLIENT
===================================================== */

const ElectricalChatbotClient = axios.create({
  baseURL:
    process.env.REACT_APP_ELECTRICAL_API_URL ||
    api.API_URL,
  headers: {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  },
});

ElectricalChatbotClient.interceptors.request.use((config) => {
  const latestToken = localStorage.getItem("token");
  if (latestToken) {
    config.headers["Authorization"] = `Bearer ${latestToken}`;
  }
  return config;
});

ElectricalChatbotClient.interceptors.response.use(
  (response) => response.data ?? response,
  async (error) => {
    const errorData = error.response?.data;

    if (
      error.response?.status === 401 &&
      (
        errorData?.message === "The incoming token has expired" ||
        errorData?.message === "Unauthorized"
      )
    ) {
      try {
        const newToken = await refreshToken();
        error.config.headers.Authorization = "Bearer " + newToken;
        return ElectricalChatbotClient.request(error.config);
      } catch {
        window.location.href = "/logout";
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

/* =====================================================
   API CLIENT
===================================================== */

const useAPIClient = () => {
  return {
    get: (url, params) => axios.get(url, { params }),
    create: (url, data) => axios.post(url, data),
    update: (url, data) => axios.patch(url, data),
    patch: (url, data) => axios.patch(url, data),
    put: (url, data) => axios.put(url, data),
    delete: (url, data) => axios.delete(url, { data }),
  };
};

/* =====================================================
   CHATBOT CLIENT HOOK
===================================================== */

const useChatbotClient = () => {
  return {
    get: (url, params) => ChatbotClient.get(url, { params }),
    create: (url, data) => ChatbotClient.post(url, data),
  };
};

/* =====================================================
   ELECTRICAL CHATBOT CLIENT HOOK
===================================================== */

const useElectricalChatbotClient = () => {
  return {
    get: (url, params) => ElectricalChatbotClient.get(url, { params }),
    create: (url, data) => ElectricalChatbotClient.post(url, data),
    update: (url, data) => ElectricalChatbotClient.patch(url, data),
    put: (url, data) => ElectricalChatbotClient.put(url, data),
    delete: (url, data) => ElectricalChatbotClient.delete(url, { data }),
  };
};

/* =====================================================
   LOGGED IN USER
===================================================== */

const getLoggedinUser = () => {
  const user = localStorage.getItem("authUser");
  return user ? JSON.parse(user) : null;
};

/* =====================================================
   EXPORTS
===================================================== */

export {
  useAPIClient,
  useChatbotClient,
  useElectricalChatbotClient,
  setAuthorization,
  getLoggedinUser,
  S3Client,
};
